import { spawnSync } from "node:child_process";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import type { Verdict } from "@m8t-stack/api-contract";

const DECISIONS: ReadonlySet<string> = new Set(["promote", "reject", "needs_review"]);
const JUDGE_STATUSES: ReadonlySet<string> = new Set(["ok", "skipped", "unavailable"]);

/** Parse the brain-eval core's `--json` stdout into a typed Verdict.
 *  Fails closed: $BRAIN_EVAL_BIN is operator-overridable, so the wire shape is validated
 *  at runtime — an unknown decision/status or missing field is never trusted. */
export function parseVerdict(stdout: string): Verdict {
  let raw: unknown;
  try {
    raw = JSON.parse(stdout);
  } catch (e) {
    throw new LocalCliError({
      code: "BRAIN_EVAL_BAD_OUTPUT",
      message: `brain-eval emitted non-JSON stdout: ${e instanceof Error ? e.message : String(e)}`,
    });
  }
  const v = raw as Partial<Verdict> | null;
  if (
    v === null ||
    typeof v !== "object" ||
    typeof v.candidatePath !== "string" ||
    typeof v.rationale !== "string" ||
    typeof v.decision !== "string" ||
    !DECISIONS.has(v.decision) ||
    typeof v.judgeStatus !== "string" ||
    !JUDGE_STATUSES.has(v.judgeStatus) ||
    !Array.isArray(v.findings)
  ) {
    throw new LocalCliError({
      code: "BRAIN_EVAL_BAD_OUTPUT",
      message: "brain-eval emitted a malformed Verdict (unknown decision/status or missing fields) — refusing to trust it",
    });
  }
  return v as Verdict;
}

export class EvalSkillCommand extends M8tCommand {
  static paths = [["eval", "skill"]];
  static usage = Command.Usage({
    description:
      "Vet one inbox skill candidate: promote / reject / needs_review. " +
      "Shells out to the Python `brain-eval` core (override its path with $BRAIN_EVAL_BIN).",
  });

  candidate = Option.String();
  skillsDir = Option.String("--skills-dir");
  noJudge = Option.Boolean("--no-judge", false);
  deployment = Option.String("--deployment");
  output = Option.String("--output");

  executeCommand(): Promise<number> {
    return Promise.resolve(this._runCommand());
  }

  private _runCommand(): number {
    const candidate = typeof this.candidate === "string" ? this.candidate : undefined;
    const skillsDir = typeof this.skillsDir === "string" ? this.skillsDir : undefined;
    if (!candidate) {
      throw new LocalCliError({ code: "USAGE", message: "<path> positional argument is required" });
    }
    if (!skillsDir) {
      throw new LocalCliError({ code: "USAGE", message: "--skills-dir <dir> is required" });
    }

    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);

    const bin = process.env.BRAIN_EVAL_BIN ?? "brain-eval";
    const args = ["skill", candidate, "--skills-dir", skillsDir, "--json"];
    // `=== true`: clipanion's Option descriptor is truthy until argv parse, so a bare
    // check would pass --no-judge by default under test construction.
    if (this.noJudge === true) args.push("--no-judge");
    if (typeof this.deployment === "string") args.push("--deployment", this.deployment);

    const res = spawnSync(bin, args, { encoding: "utf-8" });
    if (res.error) {
      throw new LocalCliError({
        code: "BRAIN_EVAL_SPAWN",
        message: `could not run '${bin}' — set $BRAIN_EVAL_BIN to the brain-eval entrypoint (e.g. eval/.venv/bin/brain-eval): ${res.error.message}`,
      });
    }
    if (res.status !== 0) {
      throw new LocalCliError({
        code: "BRAIN_EVAL_FAILED",
        message: (res.stderr || "brain-eval exited non-zero").trim(),
      });
    }

    const verdict = parseVerdict(res.stdout);

    if (mode === "json") {
      this.context.stdout.write(renderJson(verdict) + "\n");
      return 0;
    }
    this.context.stdout.write(
      `${colors.field(verdict.decision.toUpperCase())}  (${verdict.judgeStatus})  ${verdict.candidatePath}\n`,
    );
    this.context.stdout.write(`  ${verdict.rationale}\n`);
    const defaulted = verdict.judge?.flags?.defaulted;
    const missingTags: string[] = [];
    if (defaulted?.safety) missingTags.push("safety");
    if (defaulted?.collision) missingTags.push("collision");
    if (missingTags.length > 0) {
      this.context.stdout.write(`  ⚠ judge tag(s) defaulted, not explicit: ${missingTags.join(", ")}\n`);
    }
    return 0;
  }
}
